/**
 * ShortcutsModal — keyboard-shortcut reference Dialog (slice 642).
 *
 * Triggered globally from AppShell via the `?` key. Lists every bound
 * shortcut grouped by scope (Global / List pages / Detail pages). Hardcoded
 * key labels (Cmd/Ctrl+K, F, E, etc.) match the actual handlers in
 * AppShell, list pages and detail pages — see corresponding aria-keyshortcuts
 * attributes app-wide.
 *
 * Closes on Esc (MUI Dialog default) or the explicit Close button.
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  keys: string[];
  labelKey: string;
}

interface ShortcutScope {
  titleKey: string;
  rows: ShortcutRow[];
}

const SCOPES: ShortcutScope[] = [
  {
    titleKey: 'SHORTCUTS.scopeGlobal',
    rows: [
      { keys: ['Cmd/Ctrl+K'], labelKey: 'SHORTCUTS.openCommandPalette' },
      { keys: ['?'], labelKey: 'SHORTCUTS.openShortcuts' },
      { keys: ['Cmd/Ctrl+,'], labelKey: 'SHORTCUTS.openPreferences' },
    ],
  },
  {
    titleKey: 'SHORTCUTS.scopeListPages',
    rows: [
      { keys: ['/'], labelKey: 'SHORTCUTS.focusSearch' },
      { keys: ['Cmd/Ctrl+A'], labelKey: 'SHORTCUTS.selectAll' },
      { keys: ['Esc'], labelKey: 'SHORTCUTS.clearSelection' },
      { keys: ['Alt+←', 'Alt+→'], labelKey: 'SHORTCUTS.paginate' },
      { keys: ['R'], labelKey: 'SHORTCUTS.refresh' },
    ],
  },
  {
    titleKey: 'SHORTCUTS.scopeDetailPages',
    rows: [
      { keys: ['Esc'], labelKey: 'SHORTCUTS.goBack' },
      { keys: ['F'], labelKey: 'SHORTCUTS.toggleFavorite' },
      { keys: ['E'], labelKey: 'SHORTCUTS.editRecord' },
      { keys: ['P'], labelKey: 'SHORTCUTS.print' },
      { keys: ['Shift+Delete'], labelKey: 'SHORTCUTS.deleteRecord' },
      { keys: ['Cmd/Ctrl+S'], labelKey: 'SHORTCUTS.save' },
      { keys: ['Alt+1', 'Alt+9'], labelKey: 'SHORTCUTS.jumpToTab' },
    ],
  },
];

export function ShortcutsModal({ open, onClose }: ShortcutsModalProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="shortcuts-modal-title"
      aria-describedby="shortcuts-modal-description"
    >
      <DialogTitle id="shortcuts-modal-title">{t('SHORTCUTS.title')}</DialogTitle>
      <DialogContent dividers id="shortcuts-modal-description">
        {SCOPES.map((scope) => (
          <Box key={scope.titleKey} sx={{ mb: 2.5, '&:last-of-type': { mb: 0 } }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t(scope.titleKey)}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {scope.rows.map((row) => (
                    <TableRow key={`${scope.titleKey}-${row.labelKey}-${row.keys.join('+')}`}>
                      <TableCell sx={{ width: '40%', borderBottom: 'none', py: 0.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {row.keys.map((k) => (
                            <Chip
                              key={k}
                              label={k}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                        <Typography variant="body2">{t(row.labelKey)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-keyshortcuts="Escape">
          {t('actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
